import { useState } from 'preact/hooks';
import type { BookEdits } from './actions';
import { previewOf, readKindlePaste, sameBook, type Preview } from './kindle';
import { copyName, readsInOrder } from './model';
import { Sheet } from './reading';
import { BackLink } from '../ui/BackLink';
import { ChoiceChips } from '../ui/bits';
import type { BookData } from './types';

// Paste from Kindle… (book page Part 6; boards 4 and 5, decided Oct 20 and 21; Kristi's notebook
// page route, Oct 27): paste, preview with a tick box on every entry, then one Save with one Undo.

/** The read pasted entries go on: the current one, else the most recent, else none (Kristi, Oct 27). */
export function pasteRead(book: BookData): { n: number; label: string } | null {
  const ordered = readsInOrder(book.reads);
  if (!ordered.length) return null;
  const open = ordered.findIndex((r) => r.outcome === 'open');
  const i = open >= 0 ? open : ordered.length - 1;
  const r = ordered[i]!;
  const copies = book.copies.filter((c) => r.copies.some((rc) => rc.copy_id === c.id)).map((c) => copyName(book, c));
  return { n: i + 1, label: [`Read ${i + 1}`, ...copies].join(' · ') };
}

const short = (s: string, n = 140) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

export function PasteKindleSheet({ book, edits, onClose }: { book: BookData; edits: BookEdits; onClose: () => void }) {
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [found, setFound] = useState({ quotes: 0, notes: 0 });
  const [off, setOff] = useState<Set<string>>(new Set());
  const [update, setUpdate] = useState<Set<string>>(new Set());
  const [problem, setProblem] = useState<string | null>(null);
  const [other, setOther] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const target = pasteRead(book);
  const before = book.quotes.some((q) => q.source === 'kindle');

  const read = () => {
    const p = readKindlePaste(text, book.work.title);
    if (!p.source || (!p.quotes.length && !p.notes.length)) {
      setProblem(
        p.source === 'clippings'
          ? `No highlights for ${book.work.title} in that file. My Clippings.txt has every book: check the title matches.`
          : 'No Kindle highlights found in that. Copy the whole notebook page for the book (select all, then copy), and paste it here.',
      );
      return;
    }
    setProblem(null);
    setOther(p.title && !sameBook(p.title, book.work.title) ? p.title : null);
    setFound({ quotes: p.quotes.length, notes: p.notes.length + p.quotes.filter((q) => q.note).length });
    setPreview(
      previewOf(p, {
        quotes: book.quotes.map((q) => ({ id: q.id, body: q.body, location: q.location })),
        notes: book.readingNotes.map((n) => ({ id: n.id, body: n.body, location: n.location, quote_id: n.quote_id })),
        leftOut: book.kindleLeftOut,
      }),
    );
    setOff(new Set());
    setUpdate(new Set());
  };

  if (!preview)
    return (
      <Sheet title={`Paste from Kindle: ${book.work.title}`} sub="Nothing is saved until you’ve seen what was found." onClose={onClose}>
        <ol class="bk-howto">
          <li>
            Open{' '}
            <a class="btn-link" href="https://read.amazon.com/notebook" target="_blank" rel="noopener noreferrer">
              read.amazon.com/notebook ↗
            </a>{' '}
            and pick the book.
          </li>
          <li>Select all the text on the page (⌘A on a Mac, Ctrl+A on Windows, or drag across it on a phone or iPad), and copy.</li>
          <li>Paste them here.</li>
        </ol>
        <p class="bk-quiet">
          The Export notebook email and a Kindle’s My Clippings.txt work too (only this book’s entries are read). Kindle’s export can cut long highlights short, and
          Audible clips aren’t included.
        </p>
        <label class="bk-field">
          <span class="bk-label">Your highlights and notes</span>
          <textarea aria-label="Paste your Kindle highlights" rows={8} value={text} onInput={(e) => setText((e.target as HTMLTextAreaElement).value)} />
        </label>
        {problem ? <div class="heads-up">{problem}</div> : null}
        <div class="bk-buttons">
          <button type="button" class="btn-main" disabled={!text.trim()} onClick={read}>
            Read it
          </button>
          <button type="button" class="btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </Sheet>
    );

  const quotesKept = preview.quotes.filter((q) => !off.has(q.key));
  const newQuotes = quotesKept.filter((q) => q.isNew).length;
  const newNotes = quotesKept.filter((q) => q.note && q.noteState === 'new').length + preview.notes.filter((n) => !off.has(n.key)).length;
  const updates = preview.quotes.filter((q) => q.noteState === 'changed' && update.has(q.key));
  const nothing = !preview.quotes.length && !preview.notes.length;
  const toggle = (key: string) => {
    const next = new Set(off);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setOff(next);
  };
  const save = async () => {
    setBusy(true);
    const details = {
      quotes: quotesKept
        .filter((q) => q.isNew || q.noteState === 'new')
        .map((q) =>
          q.isNew
            ? { body: q.body, location: q.location, chapter: q.chapter, on: q.on, key: q.key, note: q.note && q.noteState !== 'saved' ? q.note : null }
            : { quote_id: q.savedQuoteId, location: q.location, note: q.note },
        ),
      notes: preview.notes.filter((n) => !off.has(n.key)).map((n) => ({ body: n.body, location: n.location, on: n.on, key: n.key })),
      updates: updates.map((q) => ({ note_id: q.savedNoteId, body: q.note!.body })),
      left_out: [...preview.quotes.filter((q) => q.isNew && off.has(q.key)), ...preview.notes.filter((n) => off.has(n.key))].map((x) => ({ key: x.key, excerpt: short(x.body, 120) })),
    };
    const ok = await edits.act('kindle_paste', { details }, `Added ${newQuotes} ${newQuotes === 1 ? 'quote' : 'quotes'} and ${newNotes} ${newNotes === 1 ? 'reading note' : 'reading notes'} from Kindle`);
    setBusy(false);
    if (ok) onClose();
  };
  const folded = [preview.alreadySaved ? `${preview.alreadySaved} already saved` : null, preview.leftOutBefore ? `${preview.leftOutBefore} left out last time` : null].filter(Boolean);
  return (
    <Sheet
      title={`Paste from Kindle: ${book.work.title}`}
      sub={`Found ${found.quotes} ${found.quotes === 1 ? 'highlight' : 'highlights'} and ${found.notes} ${found.notes === 1 ? 'note' : 'notes'}${before ? ` · ${preview.quotes.filter((q) => q.isNew).length + preview.notes.length} new` : ''}.`}
      onClose={onClose}
      back={<BackLink where="the paste" onClick={() => setPreview(null)} />}
    >
      {other ? (
        <div class="heads-up" role="alert">
          These look like the notes for <strong>{other}</strong>, not {book.work.title}. Check the book you picked on the notebook page; save only if they’re right.
        </div>
      ) : null}
      {nothing ? (
        <div class="heads-up">Nothing new: everything in this paste is already saved{preview.leftOutBefore ? ' or was left out last time' : ''}.</div>
      ) : (
        <>
          <div class="bk-buttons">
            <button type="button" class="btn-link" onClick={() => setOff(new Set())}>
              Tick all
            </button>
            <button type="button" class="btn-link" onClick={() => setOff(new Set([...preview.quotes.filter((q) => q.isNew).map((q) => q.key), ...preview.notes.map((n) => n.key)]))}>
              Untick all
            </button>
          </div>
          <ol class="bk-paste-list" aria-label="What was found">
            {preview.quotes.map((q) => (
              <li key={q.key} class={off.has(q.key) ? 'off' : ''}>
                <label class="bk-check">
                  <input type="checkbox" checked={!off.has(q.key)} onChange={() => toggle(q.key)} aria-label={`Keep: ${short(q.body, 60)}`} />
                  <span>
                    <span class="bk-kind teal">{q.isNew ? 'Quote' : 'Saved quote'}</span> {short(q.body)} <span class="bk-quiet">{q.location}</span>
                    {before && q.isNew ? <span class="bk-kind added">NEW</span> : null}
                  </span>
                </label>
                {q.note && q.noteState !== 'saved' ? (
                  <div class="bk-paste-note">
                    <span class="bk-kind teal">Reading note</span> {short(q.note.body)} <span class="bk-quiet">linked to this quote</span>
                    {q.noteState === 'changed' ? (
                      <ChoiceChips<boolean>
                        class="bk-dialog-chips"
                        label="Changed in Kindle"
                        small
                        lead={<span class="bk-quiet">Changed in Kindle:</span>}
                        choices={[
                          [true, 'Update mine'],
                          [false, 'Keep mine'],
                        ]}
                        value={update.has(q.key)}
                        onPick={(u) => setUpdate(u ? new Set([...update, q.key]) : new Set([...update].filter((k) => k !== q.key)))}
                      />
                    ) : null}
                  </div>
                ) : null}
              </li>
            ))}
            {preview.notes.map((n) => (
              <li key={n.key} class={off.has(n.key) ? 'off' : ''}>
                <label class="bk-check">
                  <input type="checkbox" checked={!off.has(n.key)} onChange={() => toggle(n.key)} aria-label={`Keep: ${short(n.body, 60)}`} />
                  <span>
                    <span class="bk-kind teal">Reading note</span> {short(n.body)} <span class="bk-quiet">{n.location}</span>
                  </span>
                </label>
              </li>
            ))}
          </ol>
        </>
      )}
      {folded.length ? <p class="bk-quiet">{folded.join(' · ')} · skipped</p> : null}
      <p class="bk-quiet">
        Highlight colours are dropped. {target ? `They go on ${target.label}.` : 'They’re kept on the book (no read yet).'} Left out stays left out next time.
      </p>
      <div class="bk-buttons">
        {!nothing ? (
          <button type="button" class="btn-main" disabled={busy || (!newQuotes && !newNotes && !updates.length)} onClick={() => void save()}>
            Save {newQuotes} {newQuotes === 1 ? 'quote' : 'quotes'} and {newNotes} {newNotes === 1 ? 'reading note' : 'reading notes'}
          </button>
        ) : null}
        <button type="button" class="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Sheet>
  );
}
